import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  Button,
} from "reactstrap";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import Breadcrumb from "../../components/Common/Breadcrumb";
import Utils from "../Utility";
import { post, put } from "../../helpers/api_helper";
import Loader from "../../components/Loader";
import DataTable from "react-data-table-component";
import { AvField, AvForm } from "availity-reactstrap-validation";
import { toast } from "react-toastify";

const Notice = (props) => {
  const { user } = props;
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [notices, setNotices] = useState([]);
  const [columns, setColumns] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [currentNotice, setCurrentNotice] = useState(null);

  const btnGradient = `linear-gradient(135deg, ${Utils.themeColor} 0%, #8b0000 100%)`;

  useEffect(() => {
    if (user) {
      setCurrentPage(1);
      getNotices(1, perPage, search);
    }
  }, [user, search]);

  const getNotices = (page = currentPage, limit = perPage, searchQuery = search) => {
    setTableLoading(true);
    const body = {
      token: user?.token,
      page,
      limit,
      ...(searchQuery ? { search: searchQuery } : {})
    };

    post("/notice/list", body)
      .then((json) => {
        setTableLoading(false);
        setLoading(false);
        if (json?.statusCode == 200) {
          const rows = json?.data || [];
          if (typeof json?.total === "number") {
            setNotices(rows);
            setTotalRows(json.total);
          } else {
            setTotalRows(rows.length);
            const startIndex = (page - 1) * limit;
            setNotices(rows.slice(startIndex, startIndex + limit));
          }
        }
      })
      .catch((error) => {
        setTableLoading(false);
        setLoading(false);
        console.error("Error while getting notices", error);
      });
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    getNotices(page, perPage, search);
  };

  const handlePerRowsChange = (newPerPage, page) => {
    setPerPage(newPerPage);
    setCurrentPage(page);
    getNotices(page, newPerPage, search);
  };

  const handleUpdateNoticeStatus = (val, item) => {
    const body = {
      noticeId: item?._id,
      token: user?.token,
      isActive: val === "Active"
    };
    put("/notice/update", body)
      .then((res) => {
        if (res?.statusCode == 200) {
          toast.success(res?.message || "Status updated");
          getNotices(currentPage, perPage, search);
        } else {
          toast.error(res?.error || "Failed to update status");
        }
      })
      .catch((err) => {
        toast.error("Failed to update status");
        console.error(err);
      });
  };

  const handleEditNotice = (item) => {
    setCurrentNotice(item);
    setIsFormOpen(true);
  };

  const handleDeleteNotice = (item) => {
    post("/notice/delete", { noticeId: item?._id, token: user?.token })
      .then((res) => {
        if (res?.statusCode == 200) {
          toast.success(res?.message || "Notice deleted");
          getNotices(currentPage, perPage, search);
        } else {
          toast.error(res?.error || "Failed to delete notice");
        }
      })
      .catch((err) => {
        toast.error("Failed to delete notice");
        console.error(err);
      });
  };

  const handleValidSubmit = (event, values) => {
    if (!user) {
      toast.error("Unauthorized!");
      return;
    }
    setLoading(true);
    if (currentNotice) {
      const body = {
        ...values,
        noticeId: currentNotice?._id,
        token: user?.token
      };
      put("/notice/update", body)
        .then((json) => {
          setLoading(false);
          if (json?.statusCode == 200) {
            toast.success(json?.message || "Notice updated");
            setIsFormOpen(false);
            setCurrentNotice(null);
            getNotices(currentPage, perPage, search);
          } else {
            toast.error(json?.error || "Failed to update notice");
          }
        })
        .catch((err) => {
          setLoading(false);
          toast.error("Failed to update notice");
          console.error(err);
        });
    } else {
      const body = {
        ...values,
        token: user?.token
      };
      post("/notice/add", body)
        .then((json) => {
          setLoading(false);
          if (json?.statusCode == 200) {
            toast.success(json?.message || "Notice added");
            setIsFormOpen(false);
            setCurrentNotice(null);
            setCurrentPage(1);
            getNotices(1, perPage, search);
          } else {
            toast.error(json?.error || "Failed to add notice");
          }
        })
        .catch((err) => {
          setLoading(false);
          toast.error("Failed to add notice");
          console.error(err);
        });
    }
  };

  const formatDate = (dateVal) => {
    if (!dateVal) return "—";
    const d = new Date(dateVal);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
  };

  useEffect(() => {
    setColumns([
      {
        name: "Title",
        selector: (row) => row.title,
      },
      {
        name: "Description",
        cell: (row) => (
          <span title={row?.description}>
            {row?.description?.length > 45
              ? row.description.substring(0, 45) + "..."
              : row?.description}
          </span>
        ),
      },
      {
        name: "Created At",
        selector: (row) => formatDate(row.createdAt),
      },
      {
        name: "Updated At",
        selector: (row) => formatDate(row.updatedAt),
      },
      {
        name: "Status",
        ignoreRowClick: true,
        cell: (row) => (
          <AvForm>
            <div>
              <AvField
                onChange={(e) => handleUpdateNoticeStatus(e.target.value, row)}
                style={{ minWidth: 140, padding: 5, paddingLeft: 8, paddingRight: 8 }}
                type="select"
                name="isActive"
                value={row?.isActive ? "Active" : "Inactive"}
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </AvField>
            </div>
          </AvForm>
        ),
      },
      {
        name: "Action",
        ignoreRowClick: true,
        cell: (row) => (
          <>
            <Button
              onClick={() => handleEditNotice(row)}
              title="Edit"
              style={{
                background: btnGradient,
                border: "none",
                marginRight: 10,
                padding: 5,
                paddingLeft: 8,
                paddingRight: 8
              }}
            >
              <span className="mdi mdi-pencil" style={{ fontSize: 20 }}></span>
            </Button>
            <Button
              onClick={() => handleDeleteNotice(row)}
              title="Delete"
              style={{
                background: btnGradient,
                border: "none",
                marginRight: 10,
                padding: 5,
                paddingLeft: 8,
                paddingRight: 8
              }}
            >
              <span className="mdi mdi-trash-can" style={{ fontSize: 20 }}></span>
            </Button>
          </>
        ),
      },
    ]);
  }, [notices, currentPage, perPage, search]);

  return (
    <React.Fragment>
      <div className="page-content">
        <Loader visible={loading} />
        <Breadcrumb title={Utils.projectName} breadcrumbItem="Notice" />

        {!isFormOpen && (
          <AvForm className="form-horizontal">
            <div className="d-flex justify-content-between align-items-end mb-4">
              <div className="form-group mb-0" style={{ minWidth: 280 }}>
                <AvField
                  name="search"
                  label="Search Notice"
                  value={search}
                  className="form-control"
                  placeholder="Search by title or description..."
                  type="text"
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <Button
                type="button"
                style={{ background: btnGradient, border: "none", height: 40 }}
                onClick={() => {
                  setCurrentNotice(null);
                  setIsFormOpen(true);
                }}
              >
                Add New Notice
              </Button>
            </div>
          </AvForm>
        )}

        <Card>
          <CardBody>
            {isFormOpen ? (
              <div>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div>
                    <CardTitle className="h4">
                      {currentNotice ? "Update" : "Add New"} Notice
                    </CardTitle>
                    <p className="card-title-desc mb-0">
                      This <code>Notice</code> will be {currentNotice ? "updated" : "added"} and can be managed from here.
                    </p>
                  </div>
                  <Button
                    type="button"
                    style={{ background: btnGradient, border: "none" }}
                    onClick={() => {
                      setIsFormOpen(false);
                      setCurrentNotice(null);
                    }}
                  >
                    Close
                  </Button>
                </div>

                <AvForm
                  className="form-horizontal"
                  onValidSubmit={(e, v) => handleValidSubmit(e, v)}
                >
                  <div className="form-group mb-4">
                    <AvField
                      name="title"
                      label="Title"
                      value={currentNotice?.title || ""}
                      className="form-control"
                      placeholder="Enter Notice Title"
                      type="text"
                      required
                    />
                  </div>

                  <div className="form-group mb-4">
                    <AvField
                      name="description"
                      label="Description"
                      value={currentNotice?.description || ""}
                      className="form-control"
                      placeholder="Enter Notice Description"
                      type="textarea"
                      rows="4"
                      required
                    />
                  </div>

                  <div className="text-center mt-4">
                    <Button
                      type="submit"
                      style={{ background: btnGradient, border: "none", height: 40 }}
                    >
                      {currentNotice ? "Update Notice" : "Add Notice"}
                    </Button>
                  </div>
                </AvForm>
              </div>
            ) : (
              <DataTable
                columns={columns}
                data={notices}
                progressPending={tableLoading}
                pagination
                paginationServer
                paginationTotalRows={totalRows}
                paginationDefaultPage={currentPage}
                onChangeRowsPerPage={handlePerRowsChange}
                onChangePage={handlePageChange}
                customStyles={{
                  headCells: {
                    style: {
                      color: "black",
                      fontWeight: "bold",
                      fontSize: 15,
                    },
                  },
                }}
              />
            )}
          </CardBody>
        </Card>
      </div>
    </React.Fragment>
  );
};

const mapStatetoProps = (state) => {
  const { user } = state.Profile;
  return { user };
};

export default withRouter(connect(mapStatetoProps, {})(Notice));
